import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";

interface DrugSearchResult {
  id: number | string;
  name: string;
  price?: string | number;
  show_in_chart?: boolean;
  low_stock_threshold?: number | string;
}

interface SupplierItem {
  id: number | string;
  name: string;
}

interface ExistingBill {
  id: number;
  invoice_no: string;
  total: number;
}

interface DrugEntry {
  id: string; // temp ID for React key
  drug_id: number | null;
  drug_name: string;
  mrp: string;
  buying_price: string;
  units_per_strip: string;
  num_strips: string;
  selling_price: string;
  expiry_date: string;
  batch_number: string;
  tax_percentage: string;
  show_in_chart: boolean;
  low_stock_threshold: string;
}

const computeSellingPrice = (mrp: string, unitsPerStrip: string) => {
  const m = Number(mrp);
  const u = parseInt(unitsPerStrip, 10);
  if (!Number.isFinite(m) || m <= 0 || !Number.isFinite(u) || u <= 0) return "";
  return String(Math.round(m / u));
};

export default function StockEntryPanel() {
  const { showToast } = useToast();
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierItem | null>(null);
  const [billNumber, setBillNumber] = useState("");
  const [existingBills, setExistingBills] = useState<ExistingBill[]>([]);

  const [drugEntries, setDrugEntries] = useState<DrugEntry[]>([{
    id: crypto.randomUUID(),
    drug_id: null,
    drug_name: "",
    mrp: "",
    buying_price: "",
    units_per_strip: "10",
    num_strips: "",
    selling_price: "",
    expiry_date: "",
    batch_number: "",
    tax_percentage: "5",
    show_in_chart: false,
    low_stock_threshold: "",
  }]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DrugSearchResult[]>([]);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  
  const [isOldStock, setIsOldStock] = useState(false);

  useEffect(() => {
    apiGet("/api/pharmacy/suppliers")
      .then((res: unknown) => setSuppliers(Array.isArray(res) ? (res as SupplierItem[]) : []))
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (!selectedSupplier?.id) {
      setExistingBills([]);
      return;
    }
    apiGet(`/api/pharmacy/supplier/${encodeURIComponent(String(selectedSupplier.id))}/bills?include_paid=true`)
      .then((res: unknown) => setExistingBills(Array.isArray(res) ? (res as ExistingBill[]) : []))
      .catch(() => setExistingBills([]));
  }, [selectedSupplier?.id]);

  useEffect(() => {
    if (query.length < 2 || !activeEntryId) return;
    const t = setTimeout(() => {
      apiGet(`/api/pharmacy/drugs/search?q=${query}`)
        .then((res: unknown) => setResults(Array.isArray(res) ? (res as DrugSearchResult[]) : []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, activeEntryId]);

  const handleSelectDrug = (drug: DrugSearchResult, entryId: string) => {
    setDrugEntries(drugEntries.map(entry => {
      if (entry.id === entryId) {
        return {
          ...entry,
          drug_id: Number(drug.id),
          drug_name: drug.name,
          selling_price: String(drug.price ?? ""),
          show_in_chart: Boolean(drug.show_in_chart),
          low_stock_threshold: String(drug.low_stock_threshold ?? ""),
        };
      }
      return entry;
    }));
    setQuery("");
    setResults([]);
    setActiveEntryId(null);
  };

  const updateEntry = (entryId: string, field: keyof DrugEntry, value: any) => {
    setDrugEntries(drugEntries.map(entry => {
      if (entry.id !== entryId) return entry;
      const next = { ...entry, [field]: value } as DrugEntry;
      if (field === "mrp" || field === "units_per_strip") {
        next.selling_price = computeSellingPrice(String(next.mrp ?? ""), String(next.units_per_strip ?? ""));
      }
      return next;
    }));
  };

  const addDrugRow = () => {
    setDrugEntries([...drugEntries, {
      id: crypto.randomUUID(),
      drug_id: null,
      drug_name: "",
      mrp: "",
      buying_price: "",
      units_per_strip: "10",
      num_strips: "",
      selling_price: "",
      expiry_date: "",
      batch_number: "",
      tax_percentage: "5",
      show_in_chart: false,
      low_stock_threshold: "",
    }]);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length <= 1) {
        showToast({ message: "CSV file seems empty or invalid", type: "error" });
        return;
      }
      // Skip header
      const newEntries: DrugEntry[] = [];
      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',').map(p => p.trim());
        if (parts.length < 5) continue;
        // Expected CSV: Drug Name, Batch, MRP, Units/Strip, Strips, Buying Price, GST%, Expiry
        const [name, batch, mrp, units, strips, buying, gst, expiry] = parts;
        newEntries.push({
          id: crypto.randomUUID(),
          drug_id: null, // User must map manually if they want exact ID tracking, but string name is sent
          drug_name: name || "",
          mrp: mrp || "",
          batch_number: batch || "",
          units_per_strip: units || "10",
          num_strips: strips || "",
          buying_price: buying || "",
          tax_percentage: gst || "0",
          selling_price: computeSellingPrice(mrp || "", units || "10"),
          expiry_date: expiry || "",
          show_in_chart: false,
          low_stock_threshold: "",
        });
      }
      if (newEntries.length > 0) {
        if (drugEntries.length === 1 && !drugEntries[0].drug_name) {
          setDrugEntries(newEntries);
        } else {
          setDrugEntries([...drugEntries, ...newEntries]);
        }
        showToast({ message: `Loaded ${newEntries.length} rows from CSV`, type: "success" });
      }
    };
    reader.readAsText(file);
    // reset input
    e.target.value = '';
  };

  const removeDrugRow = (entryId: string) => {
    if (drugEntries.length === 1) {
      showToast({ message: "At least one drug entry is required", type: "error" });
      return;
    }
    setDrugEntries(drugEntries.filter(entry => entry.id !== entryId));
  };

  const calculateTotalUnits = (entry: DrugEntry) => {
    return (parseInt(entry.units_per_strip, 10) || 0) * (parseInt(entry.num_strips, 10) || 0);
  };

  const calculateLineTotal = (entry: DrugEntry) => {
    const buy = parseFloat(entry.buying_price) || 0;
    const strips = parseInt(entry.num_strips, 10) || 0;
    return buy * strips;
  };

  const calculateTaxAmount = (entry: DrugEntry) => {
    const lineTotal = calculateLineTotal(entry);
    const taxP = parseFloat(entry.tax_percentage) || 0;
    return (lineTotal * taxP) / 100;
  };

  const summary = drugEntries.reduce((acc, entry) => {
    const lineTotal = calculateLineTotal(entry);
    const taxAmt = calculateTaxAmount(entry);
    return {
      subtotal: acc.subtotal + lineTotal,
      tax: acc.tax + taxAmt,
      grandTotal: acc.grandTotal + lineTotal + taxAmt
    };
  }, { subtotal: 0, tax: 0, grandTotal: 0 });

  const buyingOverMrp = drugEntries.filter((e) => {
    const mrp = Number(e.mrp);
    const buying = Number(e.buying_price);
    if (!Number.isFinite(mrp) || !Number.isFinite(buying)) return false;
    if (mrp <= 0 || buying <= 0) return false;
    return buying > mrp;
  });

  const isBuyingOverMrp = (entry: DrugEntry) => {
    const mrp = Number(entry.mrp);
    const buying = Number(entry.buying_price);
    if (!Number.isFinite(mrp) || !Number.isFinite(buying)) return false;
    if (mrp <= 0 || buying <= 0) return false;
    return buying > mrp;
  };

  const handleSubmit = async () => {
    if (!isOldStock) {
      if (!selectedSupplier?.id) {
        showToast({ message: "Please select a supplier", type: "error" });
        return;
      }
      if (!billNumber.trim()) {
        showToast({ message: "Please enter a bill number", type: "error" });
        return;
      }
    }
    
    if (buyingOverMrp.length > 0) {
      showToast({ message: "Buying Price (Pack) cannot be greater than MRP (Pack). Please correct highlighted rows.", type: "error" });
      return;
    }

    const validDrugs = drugEntries.filter(entry =>
      entry.drug_name.trim() && entry.mrp && entry.buying_price && entry.num_strips && entry.selling_price
    );

    if (validDrugs.length === 0) {
      showToast({ message: "Please add at least one drug with all required fields", type: "error" });
      return;
    }

    try {
      const payload: any = {
        is_old_stock: isOldStock,
        manual_bill_amount: Number(summary.grandTotal.toFixed(2)),
        drugs: validDrugs.map(entry => ({
          drug_id: entry.drug_id,
          mrp: parseFloat(entry.mrp),
          buying_price: parseFloat(entry.buying_price),
          units_per_strip: parseInt(entry.units_per_strip, 10),
          num_strips: parseInt(entry.num_strips, 10),
          selling_price: parseFloat(entry.selling_price),
          expiry_date: entry.expiry_date || null,
          batch_number: entry.batch_number.trim() || null,
          tax_percentage: parseFloat(entry.tax_percentage) || 0,
          tax_amount: calculateTaxAmount(entry),
          show_in_chart: entry.show_in_chart,
          low_stock_threshold: parseInt(entry.low_stock_threshold, 10) || 0,
        })),
      };

      if (!isOldStock) {
        payload.supplier_id = selectedSupplier?.id;
        payload.bill_number = billNumber.trim();
      }

      const res = (await apiPost("/api/pharmacy/stock/add", payload)) as { ok?: boolean; bill_id?: number; drugs_updated?: number };

      if (res.ok) {
        showToast({ message: `✅ Stock Updated! ${res.drugs_updated} drug(s) added${res.bill_id ? ` to Bill #${res.bill_id}` : ''}`, type: "success" });
        // Reset form
        setBillNumber("");
        setDrugEntries([{
          id: crypto.randomUUID(),
          drug_id: null,
          drug_name: "",
          mrp: "",
          buying_price: "",
          units_per_strip: "10",
          num_strips: "",
          selling_price: "",
          expiry_date: "",
          batch_number: "",
          tax_percentage: "5",
          show_in_chart: false,
          low_stock_threshold: "",
        }]);
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      showToast({ message: "Error: " + (err?.message ?? "Unknown"), type: "error" });
    }
  };

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', padding: '0', minHeight: 'calc(100vh - 120px)' }}>
      <div className="mod-window-header" style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>📦 Inventory Stock Entry</h2>
            <div style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '8px' }}>Add multiple drugs per supplier bill</div>
          </div>
          <Button variant="primary" onClick={handleSubmit}>
            Update Inventory
          </Button>
        </div>
      </div>
      <div style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Old Stock Toggle Mode */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#e0e7ff', padding: '16px', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, color: '#3730a3' }}>
            <input 
              type="checkbox" 
              checked={isOldStock} 
              onChange={(e) => setIsOldStock(e.target.checked)} 
              style={{ width: '18px', height: '18px', accentColor: '#4f46e5' }}
            />
            🕰️ Enable Old Stock Direct Entry Mode
          </label>
          <span style={{ fontSize: '0.9rem', color: '#4f46e5' }}>
            (Skip supplier & bill linking for adding past inventory)
          </span>
        </div>

        {!isOldStock && (
          <>
            <div className="mod-card" style={{ padding: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#0f172a' }}>1. Select Supplier</h4>
              {suppliers.length === 0 ? (
                <div className="mod-empty-state" style={{ margin: 0 }}>
                  No pharmacy suppliers found. Add suppliers in Pharmacy → Pharmacy Suppliers.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {suppliers.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedSupplier(s)}
                      style={{
                        padding: '8px 16px', borderRadius: '20px', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
                        border: selectedSupplier?.id === s.id ? 'none' : '1px solid #cbd5e1',
                        background: selectedSupplier?.id === s.id ? '#3b82f6' : '#fff',
                        color: selectedSupplier?.id === s.id ? '#fff' : '#475569'
                      }}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mod-card" style={{ padding: '24px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '250px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Supplier Bill Number *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    list="existing-bills-list"
                    value={billNumber}
                    onChange={(e) => setBillNumber(e.target.value)}
                    placeholder="Select or Type Bill #"
                    className="mod-input"
                    required
                  />
                  <datalist id="existing-bills-list">
                    {existingBills.map((b) => (
                      <option key={b.id} value={b.invoice_no}>
                        {b.invoice_no} (₹{b.total})
                      </option>
                    ))}
                  </datalist>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '250px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Bill Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={summary.grandTotal.toFixed(2)}
                  readOnly
                  className="mod-input"
                  style={{ background: '#f1f5f9', color: '#64748b' }}
                />
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>Auto from Grand Total</div>
              </div>
            </div>
          </>
        )}

        <div className="mod-card" style={{ padding: 0, overflow: 'hidden' }}>
          {buyingOverMrp.length > 0 && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '16px', borderBottom: '1px solid #fca5a5', fontWeight: 500 }}>
              Buying Price (Pack) is greater than MRP (Pack) in {buyingOverMrp.length} row(s). Please correct to continue.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>{isOldStock ? '1' : '2'}. Drug Entries</h4>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <span style={{ padding: '6px 12px', background: '#e2e8f0', color: '#475569', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                  📁 Bulk Upload CSV
                </span>
                <input 
                  type="file" 
                  accept=".csv" 
                  style={{ display: 'none' }} 
                  onChange={handleFileUpload} 
                />
              </label>
              <Button variant="outline" size="sm" onClick={addDrugRow}>
                + Add Another Drug
              </Button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="dt-table" style={{ margin: 0, minWidth: '1000px' }}>
              <thead>
                <tr>
                  <th>Drug Name *</th>
                  <th>Batch #</th>
                  <th style={{ textAlign: 'right' }}>MRP (Pack) *</th>
                  <th style={{ textAlign: 'right' }}>Units/Strip</th>
                  <th style={{ textAlign: 'right' }}>Strips *</th>
                  <th style={{ textAlign: 'right' }}>Buying Price *</th>
                  <th style={{ textAlign: 'right' }}>GST %</th>
                  <th style={{ textAlign: 'right' }}>GST ₹</th>
                  <th style={{ textAlign: 'right' }}>Purchase Total</th>
                  <th style={{ textAlign: 'right' }}>Selling / Unit</th>
                  <th>Expiry</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {drugEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {!entry.drug_name ? (
                        <div style={{ position: 'relative' }}>
                          <input
                            placeholder="Search drug..."
                            value={activeEntryId === entry.id ? query : ""}
                            onFocus={() => setActiveEntryId(entry.id)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '150px' }}
                          />
                          {activeEntryId === entry.id && results.length > 0 && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', zIndex: 10, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                              {results.map((d) => (
                                <div key={d.id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }} onClick={() => handleSelectDrug(d, entry.id)}>
                                  {d.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{entry.drug_name}</strong>
                          <Button variant="ghost" size="sm" style={{ padding: '2px 6px', height: 'auto', minHeight: 'auto', color: '#dc2626' }} onClick={() => updateEntry(entry.id, "drug_name", "")}>
                            ✕
                          </Button>
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        value={entry.batch_number}
                        onChange={(e) => updateEntry(entry.id, "batch_number", e.target.value)}
                        placeholder="Batch"
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '80px', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.mrp}
                        onChange={(e) => updateEntry(entry.id, "mrp", e.target.value)}
                        placeholder="MRP"
                        style={{ padding: '6px', border: `1px solid ${isBuyingOverMrp(entry) ? '#ef4444' : '#cbd5e1'}`, borderRadius: '6px', width: '80px', fontSize: '0.85rem', textAlign: 'right', background: isBuyingOverMrp(entry) ? '#fef2f2' : '#fff' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        value={entry.units_per_strip}
                        onChange={(e) => updateEntry(entry.id, "units_per_strip", e.target.value)}
                        placeholder="10"
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '60px', fontSize: '0.85rem', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        value={entry.num_strips}
                        onChange={(e) => updateEntry(entry.id, "num_strips", e.target.value)}
                        placeholder="Qty"
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '60px', fontSize: '0.85rem', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.buying_price}
                        onChange={(e) => updateEntry(entry.id, "buying_price", e.target.value)}
                        placeholder="Buying"
                        style={{ padding: '6px', border: `1px solid ${isBuyingOverMrp(entry) ? '#ef4444' : '#cbd5e1'}`, borderRadius: '6px', width: '80px', fontSize: '0.85rem', textAlign: 'right', background: isBuyingOverMrp(entry) ? '#fef2f2' : '#fff' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.tax_percentage}
                        onChange={(e) => updateEntry(entry.id, "tax_percentage", e.target.value)}
                        placeholder="GST"
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '60px', fontSize: '0.85rem', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right', color: '#64748b', fontSize: '0.9rem' }}>
                      ₹{calculateTaxAmount(entry).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                      ₹{(calculateLineTotal(entry) + calculateTaxAmount(entry)).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        value={entry.selling_price}
                        readOnly
                        placeholder="Auto"
                        title="Auto: round(MRP / units per strip)"
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '70px', fontSize: '0.85rem', textAlign: 'right', background: '#f1f5f9', color: '#64748b' }}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={entry.expiry_date}
                        onChange={(e) => updateEntry(entry.id, "expiry_date", e.target.value)}
                        style={{ padding: '6px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', width: '130px' }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button variant="ghost" size="sm" onClick={() => removeDrugRow(entry.id)} style={{ color: '#dc2626', padding: '4px 8px' }}>
                        ✕
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ background: '#f8fafc', padding: '24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '250px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.95rem' }}>
                <span>Buying Subtotal:</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>₹{summary.subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.95rem' }}>
                <span>Total Tax:</span>
                <span style={{ fontWeight: 600, color: '#334155' }}>₹{summary.tax.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0f172a', fontSize: '1.25rem', fontWeight: 800, marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #cbd5e1' }}>
                <span>Grand Total:</span>
                <span style={{ color: '#10b981' }}>₹{summary.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
